"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, ArrowRight, MapPin } from 'lucide-react';
gsap.registerPlugin(useGSAP, ScrollTrigger);
const faqItems = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Guildford?",
  answer: "Yes. BMF Scaffolding provides full scaffolding services across Guildford, including town centre properties, surrounding residential neighbourhoods, and commercial developments throughout Surrey and the wider Home Counties."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Guildford?",
  answer: "We erect residential scaffolding, commercial access structures, temporary roof systems, edge protection, chimney scaffolding, solar access towers, and specialist industrial scaffolds for projects of all sizes."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We regularly supply tailored scaffolding for roof replacements, chimney repairs, solar panel installations, fascia maintenance, and full temporary roof covers to protect exposed buildings from weather."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We partner extensively with main contractors, roofing specialists, solar installers, developers, facilities managers, local councils, and individual homeowners."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our team conducts site surveys to engineer safe access solutions for narrow alleys, terraced properties, uneven ground, and restricted urban spaces in accordance with current safety guidelines."
}, {
  id: "item-6",
  question: "How do I request a quote in Guildford?",
  answer: "You can submit project details through our online quote request form or contact our team directly. We assess project requirements, site dimensions, and safety criteria to provide a clear, itemised quotation."
}];
export default function GuildfordFaq() {
  const containerRef = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".faq-header", {
        y: 24
      });
      gsap.to(".faq-header", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".faq-content", {
        y: 30
      });
      gsap.to(".faq-content", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        delay: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".faq-aside", {
        y: 30
      });
      gsap.to(".faq-aside", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        delay: 0.25,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".faq-header", ".faq-content", ".faq-aside"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="guildfordfaq" ref={containerRef} data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header Block */}
        <div className="faq-header max-w-3xl mb-12 md:mb-16" style={{
        opacity: 0
      }}>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="outline" className="bg-muted text-foreground border-border px-3 py-1 font-medium">
              <MapPin className="size-3 text-primary mr-1" />
              Guildford & Surrey
            </Badge>
            <Badge variant="outline" className="bg-muted text-foreground border-border px-3 py-1 font-medium">
              <ShieldCheck className="size-3 text-primary mr-1" />
              NASC & CHAS Accredited
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground tracking-tight mb-4">
            Frequently asked questions about scaffolding in Guildford
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Clear facts on regional coverage, restricted site access, safety standards,
            and obtaining a quotation for your project.
          </p>
        </div>

        {/* Two-Column Grid: FAQ List + Sidebar CTA Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main FAQ Accordion */}
          <div className="faq-content lg:col-span-8 bg-card border border-border rounded-xl p-6 sm:p-8 shadow-xs" style={{
          opacity: 0
        }}>
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full divide-y divide-border">
              {faqItems.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="border-none py-1 first:pt-0 last:pb-0">
                  <AccordionTrigger className="text-left font-semibold text-foreground text-base sm:text-lg hover:no-underline py-4">
                    <span className="flex items-start gap-3">
                      <HelpCircle className="size-5 text-primary shrink-0 mt-0.5" />
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8 pr-2 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Supportive Sidebar Box */}
          <div className="faq-aside lg:col-span-4 flex flex-col gap-6" style={{
          opacity: 0
        }}>
            <div className="bg-secondary text-secondary-foreground rounded-xl p-6 sm:p-8 shadow-sm border border-secondary">
              <h3 className="text-xl font-bold font-default mb-3 text-secondary-foreground">
                Need a tailored site survey?
              </h3>
              <p className="text-sm text-secondary-foreground/90 leading-relaxed mb-6">
                Tell us about your building location, scope of work, and required
                access specifications. Our scaffolding specialists will review your
                requirements.
              </p>
              <div className="flex flex-col gap-3">
                <Button asChild variant="default" size="lg" className="w-full justify-between">
                  <Link to="/ContactRequestQuotePage">
                    <span>Request a quote</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full justify-between border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10">
                  <Link to="/AreasWeCoverPage">
                    <span>View all areas covered</span>
                    <MapPin className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 shadow-xs">
              <h4 className="text-base font-semibold text-foreground mb-2 flex items-center gap-2">
                <PhoneCall className="size-4 text-primary" />
                <span>Direct consultation</span>
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Working with main contractors, roofing teams, solar specialists, and
                homeowners with safety-first compliance across Guildford.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}